/**
 * Client-side mirror of the backend's UI-edit signature reconciliation
 * (`SendEmailActionHandler.reconcile_edited_payload`, edit_source="ui").
 *
 * The backend records `signature_disposition = "user_removed"` when a human
 * edit saves an `"inserted"` draft whose `body_html` no longer carries the
 * signature marker block. It rewrites the STORED payload — and the client's
 * approve-time snapshot echoes the payload it holds, so unless the editor
 * derives the same verdict locally the two diverge and approve fails with
 * 409 STALE_ACTIONS on every edited-then-approved signed draft (org 49,
 * 2026-08-21 → 2026-08-25). Deriving it here keeps what the editor sends,
 * what the server stores, and what approve echoes byte-identical.
 *
 * Shared by BOTH signature-aware editing surfaces — the Action Center's
 * `EmailActionPreview` (`EmailContent`) and the floating composer's
 * `EmailComposePanel` (`ComposeContent`) — hence the structural
 * `SignatureAwareContent` bound rather than a concrete content type.
 */

import {
  SIGNATURE_BLOCK_MARKER_ATTR,
  SIGNATURE_DELIMITER_HTML,
} from '@/lib/reasoning/components/email-editor/constants';
import {
  htmlToPlainText,
  plainToHtml,
} from '@/lib/reasoning/components/email-editor/plateEmailSerializer';

export type SignatureDisposition = 'none' | 'inserted' | 'user_removed';

interface SignatureAwareContent {
  signature_disposition?: SignatureDisposition;
  body_html?: string;
}

// Same shape as `apps/email/actions/rendering.py::_SIGNATURE_MARKER_OPEN_RE`:
// the marker attribute on a <div> open tag, case-insensitive.
const SIGNATURE_MARKER_OPEN_RE = new RegExp(
  `<div\\b[^>]*\\b${SIGNATURE_BLOCK_MARKER_ATTR}\\b[^>]*>`,
  'i'
);

export function signatureMarkerPresent(
  bodyHtml: string | undefined | null
): boolean {
  if (!bodyHtml) {
    return false;
  }
  return SIGNATURE_MARKER_OPEN_RE.test(bodyHtml);
}

/**
 * Returns `content` with `signature_disposition` reconciled against its
 * `body_html`: an `"inserted"` draft whose marker block is gone becomes
 * `"user_removed"`. Every other state is returned as the SAME reference —
 * the backend never flips `"user_removed"` back to `"inserted"` on a UI edit
 * (never re-append a deliberately deleted signature), and `"none"` has
 * nothing to reconcile.
 */
export function reconcileSignatureDisposition<T extends SignatureAwareContent>(
  content: T
): T {
  if (
    content.signature_disposition !== 'inserted' ||
    signatureMarkerPresent(content.body_html)
  ) {
    return content;
  }
  return { ...content, signature_disposition: 'user_removed' };
}

/**
 * Wrap a resolved signature for insertion into a draft body.
 *
 * Byte-compatible with the server's `wrap_signature_block`
 * (`apps/email/services/body_composition.py`) so a client-seeded block is
 * indistinguishable from a server-minted one. The delimiter sits INSIDE the
 * wrapper so deleting the block takes the `--` with it — otherwise removing
 * the signature would leave an orphan `--` paragraph in the sent mail.
 * `includeDelimiter` is false for Outlook, whose clients don't recognize the
 * Gmail-style `--` signature convention.
 */
export function wrapSignatureBlock(
  html: string,
  includeDelimiter: boolean
): string {
  const delimiter = includeDelimiter ? SIGNATURE_DELIMITER_HTML : '';
  return `<div ${SIGNATURE_BLOCK_MARKER_ATTR}="1">${delimiter}${html}</div>`;
}

/**
 * Compose a draft body with a signature block appended, for the
 * "add signature back" flow shared by `EmailComposePanel` and
 * `EmailActionPreview`.
 *
 * A legacy/plain-text draft carries `body` but no `body_html`; appending the
 * signature to an empty `body_html` would send only the signature and drop
 * the draft text. Falls back to `plainToHtml(content.body)` in that case so
 * the draft text is preserved — this is the exact divergence that let
 * `EmailActionPreview`'s copy of this logic silently drop plain-text-only
 * drafts while `EmailComposePanel`'s copy handled them correctly.
 */
export function appendSignatureBlock(
  content: { body_html?: string; body?: string },
  signatureHtml: string,
  includeDelimiter: boolean
): { body_html: string; body: string } {
  const baseHtml = content.body_html || plainToHtml(content.body ?? '');
  const bodyHtml = `${baseHtml}${wrapSignatureBlock(signatureHtml, includeDelimiter)}`;
  return { body_html: bodyHtml, body: htmlToPlainText(bodyHtml) };
}
